import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useGetTelehealthSessionInfoCallback } from '../../hooks';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { trySafely } from '../../utils';
import { normalizeVideoPlatform } from '../../video-sdk/videoPlatform';
import { admissionTrackingHistory } from './admission/analytics/AdmissionTrackingHistory';

export function useTrackJoiningSessionLoaderViewedEvent(isLoading: boolean) {
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();
  const getVideoPlatform = useVideoPlatformCallback();
  const { userViewed } = useTrackEvent();
  const hasLoggedRef = useRef(false);

  useEffect(
    function resetLogWhenLeavingLoading() {
      if (!isLoading) {
        hasLoggedRef.current = false;
      }
    },
    [isLoading],
  );

  useEffect(
    function emitJoiningSessionLoaderViewed() {
      if (!isLoading || hasLoggedRef.current) return;
      hasLoggedRef.current = true;
      const resolvedPatientShortId = patientShortId ?? '';
      const resolvedProviderShortId = providerShortId ?? '';
      void (async () => {
        const { data } =
          (patientShortId && providerShortId
            ? await trySafely(
                getTelehealthSessionInfo({
                  variables: { patientShortId, providerShortId },
                }),
              )
            : {}) ?? {};
        userViewed(EventTypeEnum.CLIENT_JOINING_SESSION_LOADER_VIEWED, {
          patientShortId: resolvedPatientShortId,
          providerShortId: resolvedProviderShortId,
          appointmentShortId: data?.telehealthSessionInfo?.appointment?.shortId ?? '',
          source: normalizeVideoPlatform(getVideoPlatform()),
          txId: admissionTrackingHistory.getTransactionId() ?? '',
        });
      })();
    },
    [getTelehealthSessionInfo, getVideoPlatform, isLoading, patientShortId, providerShortId, userViewed],
  );
}
